import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import apiConfig from '../apiConfig';
import './Pages.css';

const OrderTracking = () => {
    const { token } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchOrderDetails();
    }, [token]);

    const fetchOrderDetails = async () => {
        try {
            const response = await axios.get(`${apiConfig.baseURL}/orders/track/${token}`);

            if (response.data.success) {
                setOrder(response.data.order);
            } else {
                setError('Order not found or tracking link expired');
            }
        } catch (err) {
            setError('Failed to load order details. Please check your tracking link.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending_payment: '#fbbf24',
            paid: '#10b981',
            processing: '#3b82f6',
            shipped: '#8b5cf6',
            delivered: '#059669',
            cancelled: '#ef4444'
        };
        return colors[status] || '#6b7280';
    };

    const getStatusText = (status) => {
        const texts = {
            pending_payment: 'Pending Payment',
            paid: 'Paid',
            processing: 'Processing',
            shipped: 'Shipped',
            delivered: 'Delivered',
            cancelled: 'Cancelled'
        };
        return texts[status] || status;
    };

    if (loading) {
        return (
            <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <div className="payment-spinner" style={{ margin: '2rem auto' }}></div>
                <p>Loading order details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <h2>❌ {error}</h2>
                <Link to="/shop" className="btn btn-primary" style={{ marginTop: '2rem' }}>
                    Continue Shopping
                </Link>
            </div>
        );
    }

    return (
        <div className="container" style={{ padding: '4rem 2rem' }}>
            <h1>Order Tracking</h1>

            <div className="order-tracking-container" style={{ maxWidth: '800px', margin: '2rem auto' }}>
                {/* Order Status */}
                <div style={{
                    background: '#f8f9fa',
                    padding: '2rem',
                    borderRadius: '8px',
                    marginBottom: '2rem',
                    textAlign: 'center'
                }}>
                    <h2>Order #{order.orderNumber}</h2>
                    <div style={{
                        display: 'inline-block',
                        padding: '0.5rem 1.5rem',
                        borderRadius: '20px',
                        background: getStatusColor(order.status),
                        color: 'white',
                        fontWeight: 'bold',
                        marginTop: '1rem'
                    }}>
                        {getStatusText(order.status)}
                    </div>
                    <p style={{ marginTop: '1rem', color: '#666' }}>
                        Ordered on {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                </div>

                {/* Order Items */}
                <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <h3>Order Items</h3>
                    {order.items.map((item, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '1rem 0',
                            borderBottom: index < order.items.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}>
                            <img
                                src={item.imageUrl}
                                alt={item.name}
                                style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', marginRight: '1rem' }}
                            />
                            <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 0.5rem 0' }}>{item.name}</h4>
                                <p style={{ margin: 0, color: '#666' }}>Quantity: {item.quantity}</p>
                            </div>
                            <p style={{ fontWeight: 'bold' }}>KSh {(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                    ))}
                </div>

                {/* Payment & Delivery Info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <h3>Payment Details</h3>
                        <p><strong>Method:</strong> M-Pesa</p>
                        <p><strong>Receipt:</strong> {order.payment.mpesaReceiptNumber}</p>
                        <p><strong>Amount:</strong> KSh {order.total.toFixed(2)}</p>
                        <p><strong>Status:</strong> {order.payment.status === 'completed' ? '✅ Paid' : '❌ Pending'}</p>
                    </div>

                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <h3>Delivery Address</h3>
                        <p>{order.delivery.address}</p>
                        <p>{order.delivery.city}</p>
                        {order.delivery.notes && <p><strong>Notes:</strong> {order.delivery.notes}</p>}
                    </div>
                </div>

                {/* Order Summary */}
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <h3>Order Summary</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Subtotal:</span>
                        <span>KSh {order.subtotal.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Delivery Fee:</span>
                        <span>KSh {order.delivery.fee.toFixed(2)}</span>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingTop: '1rem',
                        borderTop: '2px solid #e5e7eb',
                        fontWeight: 'bold',
                        fontSize: '1.2rem'
                    }}>
                        <span>Total:</span>
                        <span>KSh {order.total.toFixed(2)}</span>
                    </div>
                </div>

                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                    <Link to="/shop" className="btn btn-primary">
                        Continue Shopping
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default OrderTracking;
